import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Box,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import Header from "components/Atoms/SharedComponents/Details/header";
import DetailsList from "components/Atoms/SharedComponents/Details/detailsList";
import CustomTabs from "components/Organisms/tabs/customTabs";
import { handleTabChange } from "services/modules/utils/Helper";
import { icons } from "assets/AssetHelper";
import { useThemePalette } from "common/hooks/theme_palette";
import {
  getCustomerEmail,
  getCustomerName,
  getGiftCardName,
  getLogEndpoint,
  getLogMethod,
  getLogStatusCode,
  getOrderAmount,
  getOrderLogs,
  getOrderReference,
  isLogFailed,
} from "templates/merit/utils";

export default function MeritOrderDetails({ details }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { backgroundRed, textRed } = useThemePalette();
  const [searchParams, setSearchParams] = useState(
    new URLSearchParams(window.location.search),
  );
  const selectedTab = searchParams.get("tab");
  const [value, setValue] = useState(Number(selectedTab) || 0);

  const order = details?.merit_order ?? details;
  const logs = getOrderLogs(order);

  const orderDetailsSection = {
    head: t("merit_orders.order_information"),
    icon: icons?.details,
    details: [
      { label: t("merit_orders.col_id"), name: order?.id ?? "-" },
      {
        label: t("merit_orders.col_reference"),
        name: getOrderReference(order),
      },
      { label: t("merit_orders.col_status"), name: order?.status ?? "-" },
      {
        label: t("merit_orders.col_amount"),
        name: `${getOrderAmount(order)} ${t("products.curr_sar")}`,
      },
      {
        label: t("merit_orders.col_customer"),
        name: getCustomerName(order),
      },
      {
        label: t("merit_orders.col_email"),
        name: getCustomerEmail(order),
      },
      {
        label: t("merit_orders.col_gift_card"),
        name: getGiftCardName(order),
      },
      {
        label: t("merit_orders.col_date"),
        name: order?.created_at
          ? dayjs(order.created_at).format("DD-MM-YYYY, hh a")
          : "-",
      },
      {
        label: t("merit_orders.col_updated_at"),
        name: order?.updated_at
          ? dayjs(order.updated_at).format("DD-MM-YYYY, hh a")
          : "-",
      },
    ],
  };

  const tabs = [
    {
      title: t("merit_orders.tab_details"),
      children: (
        <Stack bgcolor="white" borderRadius="4px" mt={2}>
          <DetailsList data={orderDetailsSection} />
        </Stack>
      ),
    },
    {
      title: t("merit_orders.tab_logs"),
      children: (
        <Paper sx={{ mt: 2, overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("merit_api_logs.col_id")}</TableCell>
                <TableCell>{t("merit_api_logs.col_endpoint")}</TableCell>
                <TableCell>{t("merit_api_logs.col_method")}</TableCell>
                <TableCell>{t("merit_api_logs.col_status_code")}</TableCell>
                <TableCell>{t("merit_api_logs.col_result")}</TableCell>
                <TableCell>{t("merit_api_logs.col_date")}</TableCell>
                <TableCell>{t("merit_api_logs.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs?.length ? (
                logs.map((log) => {
                  const failed = isLogFailed(log);
                  return (
                    <TableRow key={log?.id}>
                      <TableCell>{log?.id ?? "-"}</TableCell>
                      <TableCell>{getLogEndpoint(log)}</TableCell>
                      <TableCell>{getLogMethod(log)}</TableCell>
                      <TableCell>{getLogStatusCode(log)}</TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            bgcolor: failed ? backgroundRed : "transparent",
                            color: failed ? textRed : "inherit",
                            p: failed ? "1px 8px" : 0,
                            width: "fit-content",
                            borderRadius: "4px",
                          }}
                        >
                          {failed
                            ? t("merit_api_logs.failed")
                            : t("merit_api_logs.success")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log?.created_at
                          ? dayjs(log.created_at).format("DD-MM-YYYY, hh a")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {log?.id ? (
                          <Link
                            component="button"
                            onClick={() =>
                              navigate(
                                `/merit/api-logs?type=details&id=${log.id}`,
                              )
                            }
                          >
                            {t("common.view")}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {t("merit_orders.no_logs")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      ),
    },
  ];

  return (
    <Stack>
      <Header
        title={t("merit_orders.order_details")}
        editBtn={false}
        deleteBtn={false}
      />
      <Box mt={2}>
        <CustomTabs
          tabs={tabs}
          selectedTab={value}
          handleTabChange={(event, newValue) =>
            handleTabChange({
              event,
              newValue,
              setValue,
              searchParams,
              setSearchParams,
            })
          }
        />
      </Box>
    </Stack>
  );
}
